import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Sparkles, TrendingDown, Users, Percent, Loader2 } from "lucide-react";
import { TYPE_LABELS } from "./QuestionEditor";

/** Teacher analytics for a quiz: avg, histogram, per-question success %, AI recommendation. */
export default function QuizAnalytics({ quiz }) {
  const [stats, setStats] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    api.get(`/quizzes/${quiz.id}/analytics`).then(r => setStats(r.data))
      .catch(() => toast.error("تعذر تحميل التحليلات"));
  }, [quiz.id]);

  const getAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/ai-insights`);
      setAi(data.insights);
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذر توليد التحليل الذكي");
    } finally { setAiBusy(false); }
  };

  if (!stats) return <div className="p-8 text-center text-sm text-muted-foreground">جارٍ التحميل...</div>;

  if (stats.submissions === 0) {
    return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="analytics-empty">لا توجد تسليمات بعد — ستظهر التحليلات بعد أن يحلّ الطلاب الاختبار.</div>;
  }

  const maxBucket = Math.max(1, ...stats.histogram.map(b => b.count));

  return (
    <div className="space-y-5" data-testid="quiz-analytics">
      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 border text-center">
          <Users className="w-4 h-4 mx-auto text-emerald-600 mb-1" />
          <div className="text-xl font-bold" data-testid="analytics-submissions">{stats.submissions}</div>
          <div className="text-[11px] text-muted-foreground">طالب سلّم</div>
        </Card>
        <Card className="p-3 border text-center">
          <Percent className="w-4 h-4 mx-auto text-emerald-600 mb-1" />
          <div className="text-xl font-bold" data-testid="analytics-avg">{stats.avg_score}%</div>
          <div className="text-[11px] text-muted-foreground">متوسط الدرجة</div>
        </Card>
        <Card className="p-3 border text-center">
          <TrendingDown className="w-4 h-4 mx-auto text-amber-600 mb-1" />
          <div className="text-xl font-bold">{stats.pass_rate}%</div>
          <div className="text-[11px] text-muted-foreground">نسبة النجاح (≥60%)</div>
        </Card>
      </div>

      {/* Histogram */}
      <div>
        <div className="text-sm font-semibold mb-2">توزيع النتائج</div>
        <div className="flex items-end gap-2 h-32" data-testid="analytics-histogram">
          {stats.histogram.map(b => (
            <div key={b.label} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-xs font-bold">{b.count || ""}</span>
              <div className="w-full rounded-t bg-emerald-500/80 transition-all"
                style={{ height: `${(b.count / maxBucket) * 88}px`, minHeight: b.count ? 8 : 2, opacity: b.count ? 1 : 0.2 }} />
              <span className="text-[10px] text-muted-foreground" dir="ltr">{b.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-question success */}
      <div>
        <div className="text-sm font-semibold mb-2">نسبة الإجابة الصحيحة لكل سؤال</div>
        <div className="space-y-2">
          {stats.per_question.map((pq, i) => {
            const pct = pq.success_pct ?? 0;
            const weak = pct < 40;
            return (
              <div key={pq.question_id} className={`p-2 rounded-lg border ${weak ? "border-red-300 bg-red-50/60" : ""}`}
                data-testid={`analytics-q-${i}`}>
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className="truncate flex-1">{i + 1}. {pq.text || "—"} <span className="text-muted-foreground">({TYPE_LABELS[pq.type] || pq.type})</span></span>
                  <span className={`font-bold ms-2 ${weak ? "text-red-600" : "text-emerald-700"}`}>{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full rounded-full ${weak ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                </div>
                {weak && <div className="text-[11px] text-red-600 mt-1">⚠️ سؤال ضعيف — أقل من 40% أجابوا صحيحاً</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* AI recommendation */}
      <Card className="p-4 border border-emerald-200 bg-emerald-50/40">
        <div className="flex justify-between items-center mb-2">
          <div className="text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600" />توصية المستشار التربوي (AI)</div>
          {!ai && (
            <Button size="sm" onClick={getAi} disabled={aiBusy} data-testid="analytics-ai-btn">
              {aiBusy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />جارٍ التحليل...</> : "حلّل النتائج"}
            </Button>
          )}
        </div>
        {ai
          ? <div className="text-sm leading-7 whitespace-pre-wrap" data-testid="analytics-ai-text">{ai}</div>
          : <p className="text-xs text-muted-foreground">اضغط "حلّل النتائج" ليقترح الذكاء الاصطناعي كيفية معالجة نقاط ضعف الفصل.</p>}
      </Card>
    </div>
  );
}
